/**
 * EventFeed — chronological events up to the playback cursor from the engine's
 * event store (position changes with their classification and evidence, pit
 * stops, phases, penalties). Clicking seeks to the event and selects the car.
 */
import React, { useMemo, useState } from "react";
import { ArrowRight, Radio } from "lucide-react";
import { useRaceSession } from "../RaceSessionProvider";
import { formatSessionClock } from "../playback";

const FILTERS = ["all", "passes", "pits", "race_control", "penalties"];
const TYPE_LABEL = {
    on_track_pass: "On-track pass",
    pit_cycle_position_change: "Pit cycle",
    start_position_change: "Start",
    restart_position_change: "Restart",
    retirement_related_gain: "Retirement",
    penalty_adjustment: "Penalty",
    classification_adjustment: "Classification",
    correction_induced: "Correction",
    uncertain_exchange: "Position change",
    pit_stop: "Pit stop",
    fastest_lap: "Fastest lap",
    stopped: "Stopped",
    retired: "Retired",
};
const CONFIDENCE_CLASS = { corroborated: "border-white/10 bg-white/[0.04] text-zinc-200", likely: "border-amber-400/25 bg-amber-400/10 text-amber-200", uncertain: "border-white/8 bg-white/[0.025] text-zinc-500" };

function matchesFilter(event, filter) {
    if (filter === "all") return true;
    if (filter === "passes") return event.category === "position_change";
    if (filter === "pits") return event.category === "pit";
    if (filter === "race_control") return ["phase", "info", "investigation"].includes(event.category);
    if (filter === "penalties") return event.category === "penalty" || event.type === "penalty_adjustment";
    return true;
}

export default function EventFeed({ className = "", limit = 40 }) {
    const { events, cursor, snapshot, jumpToEvent, selectedEntryId, manifest } = useRaceSession();
    const [filter, setFilter] = useState("all");
    const [onlySelected, setOnlySelected] = useState(false);
    const raceStart = manifest?.time?.race_start_seconds ?? snapshot?.clock?.race_start_seconds ?? null;
    const acronyms = useMemo(() => new Map((snapshot?.entries || []).map((row) => [row.entry_id, row.acronym])), [snapshot]);
    const visible = useMemo(() => {
        return (events || [])
            .filter((event) => event.session_seconds != null && event.session_seconds <= cursor && event.status !== "retracted")
            .filter((event) => matchesFilter(event, filter))
            .filter((event) => !onlySelected || !selectedEntryId || (event.participants || []).includes(selectedEntryId))
            .slice(-limit)
            .reverse();
    }, [events, cursor, filter, onlySelected, selectedEntryId, limit]);

    return (
        <div className={`flex min-h-0 flex-col ${className}`} data-testid="event-feed">
            <div className="mb-2 flex flex-wrap items-center gap-1.5">
                {FILTERS.map((item) => (
                    <button key={item} type="button" aria-pressed={filter === item} onClick={() => setFilter(item)} className={`rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] transition-colors ${filter === item ? "bg-white/10 text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
                        {item.replace("_", " ")}
                    </button>
                ))}
                {selectedEntryId ? (
                    <button type="button" aria-pressed={onlySelected} onClick={() => setOnlySelected((value) => !value)} className={`ml-auto rounded-full px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.18em] ${onlySelected ? "bg-f1-red/18 text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
                        {acronyms.get(selectedEntryId) || "selected"} only
                    </button>
                ) : null}
            </div>
            {visible.length ? (
                <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                    {visible.map((event) => (
                        <button key={event.id} type="button" onClick={() => jumpToEvent(event)} className="group flex w-full items-start gap-3 rounded-2xl px-2 py-2.5 text-left transition-colors hover:bg-white/[0.03]" data-testid="event-row" data-event-type={event.type}>
                            <div className="min-w-[64px] font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-600">
                                <div>{event.lap ? `Lap ${event.lap}` : "—"}</div>
                                <div className="mt-1 text-zinc-700">{formatSessionClock(event.session_seconds, raceStart)}</div>
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className="text-sm text-zinc-100">{event.title}</span>
                                    {event.category === "position_change" ? <span className={`rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.16em] ${CONFIDENCE_CLASS[event.confidence] || CONFIDENCE_CLASS.uncertain}`}>{TYPE_LABEL[event.type] || event.type} · {event.confidence}</span> : null}
                                </div>
                                {event.detail ? <div className="mt-1 text-xs leading-relaxed text-zinc-500">{event.detail}</div> : null}
                            </div>
                            <ArrowRight className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-zinc-700 transition-colors group-hover:text-zinc-300" />
                        </button>
                    ))}
                </div>
            ) : (
                <div className="rounded-2xl bg-white/[0.015] px-4 py-5 text-sm text-zinc-500">
                    <div className="mb-2 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-600">
                        <Radio className="h-3.5 w-3.5" /> Timeline
                    </div>
                    No events yet at this point of the session.
                </div>
            )}
        </div>
    );
}
